<template>
  <div class="dev-tool">
    <div v-if="isDevEnv" class="dev-tool-entry" @click="openTool">调试工具</div>
    <el-dialog title="调试工具" :visible.sync="toolVisible" width="920px">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="菜单权限" name="menuPermission">
          <div class="dev-tool-tip">
            <span v-if="parentCode !== ''">
              当前展开：{{ parentMenuTitle }}，父级菜单码 {{ parentCode }}
            </span>
            <span v-else class="dev-tool-tip-warn">{{ menuPermissionTip }}</span>
          </div>
          <el-table v-loading="menuLoading" :data="menuRows" size="small" border height="380">
            <el-table-column width="55" align="center">
              <template slot-scope="scope">
                <el-checkbox v-model="scope.row.checked" :disabled="scope.row.registered"></el-checkbox>
              </template>
            </el-table-column>
            <el-table-column prop="menuName" label="菜单名称" min-width="160"></el-table-column>
            <el-table-column label="MENU_CODE" width="170">
              <template slot-scope="scope">
                <el-input v-model="scope.row.menuCode" size="mini" :disabled="scope.row.registered"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="110" align="center">
              <template slot-scope="scope">
                <span :class="scope.row.registered ? 'dev-tool-registered' : 'dev-tool-new'">
                  {{ scope.row.registered ? '已登记' : '可新增' }}
                </span>
              </template>
            </el-table-column>
          </el-table>
          <div class="dev-tool-footer">
            <el-button size="small" @click="loadMenuPermission">重新读取</el-button>
            <el-button size="small" type="primary" :loading="submitting" @click="submitMenuPermission">提交
            </el-button>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script>
import { getFrontMenus, addFrontMenu } from 'api/menu';

// 一级菜单标题与父级菜单码的对应关系（el-submenu 的 index 与菜单码不是同一套编号）
const SUBMENU_CODES = [
  { title: '销售订单管理', code: 100 },
  { title: '第三方配送订单', code: 118 },
  { title: '采购订单管理', code: 101 },
  { title: '制单管理', code: 102 },
  { title: '分拣配送', code: 125 },
  { title: '价格管理', code: 103 },
  { title: '审核', code: 104 },
  { title: '仓库管理', code: 105 },
  { title: '客户管理', code: 107 },
  { title: '供应商管理', code: 122 },
  { title: '系统配置', code: 108 },
  { title: '生产加工', code: 124 },
  { title: '产品管理', code: 120 },
  { title: '报表', code: 113 },
  { title: '财务中心', code: 115 },
  { title: '运营中心', code: 119 },
  { title: '食品安全', code: 123 },
  { title: '设置', code: 114 },
  { title: '数据分析', code: 117 },
];

export default {
  data () {
    return {
      isDevEnv: process.env.NODE_ENV === 'development',
      toolVisible: false,
      activeTab: 'menuPermission',
      parentMenuTitle: '',
      parentCode: '',
      menuPermissionTip: '',
      menuRows: [],
      menuLoading: false,
      submitting: false,
    };
  },
  methods: {
    openTool () {
      this.toolVisible = true;
      if (this.activeTab === 'menuPermission') {
        this.loadMenuPermission();
      }
    },
    // 当前展开的一级菜单（el-menu 已开启 unique-opened，最多一个）
    getOpenedSubmenu () {
      return document.querySelector('.el-menu-demo .el-submenu.is-opened');
    },
    loadMenuPermission () {
      this.menuRows = [];
      this.parentMenuTitle = '';
      this.parentCode = '';
      this.menuPermissionTip = '';

      const submenuEl = this.getOpenedSubmenu();
      if (!submenuEl) {
        this.menuPermissionTip = '请先展开一个一级菜单';
        return;
      }

      const titleEl = submenuEl.querySelector('.el-submenu__title');
      // 未授权菜单项是 display:none，innerText 取不到文字，必须用 textContent
      const title = titleEl ? titleEl.textContent.replace(/\s+/g, '') : '';
      const submenu = SUBMENU_CODES.find((item) => item.title === title);
      this.parentMenuTitle = title;
      if (!submenu) {
        this.menuPermissionTip = `未找到「${title}」对应的父级菜单码`;
        return;
      }
      this.parentCode = submenu.code;

      const menuItems = Array.prototype.slice.call(submenuEl.querySelectorAll('.el-menu-item'));
      const menuNames = menuItems
        .map((el) => el.textContent.replace(/\s+/g, ''))
        .filter((name) => name);

      this.menuLoading = true;
      getFrontMenus({
        PAGESIZE: 100000,
        PAGEINDEX: 0,
        MENU_CODE: String(submenu.code),
      }).then((result) => {
        const rawList = result && result.length && result[0].list ? result[0].list : [];
        // 后端是模糊匹配，这里按父级菜单码前缀再过滤一次
        const list = rawList.filter((item) => String(item.MENU_CODE).indexOf(String(submenu.code)) === 0);
        this.buildMenuRows(menuNames, list);
      }).catch((error) => {
        this.menuPermissionTip = error;
        this.$message.show({ text: error, type: 'error' });
      }).then(() => {
        this.menuLoading = false;
      });
    },
    // 比对数据库记录，生成待新增行与推荐菜单码
    buildMenuRows (menuNames, list) {
      const usedCodes = {};
      list.forEach((item) => {
        usedCodes[String(item.MENU_CODE)] = true;
      });

      let seq = 0;
      const rows = menuNames.map((menuName, sortIndex) => {
        // 已登记的描述有两种写法：「二级菜单」或「一级菜单-二级菜单」
        const fullMenuName = `${this.parentMenuTitle}-${menuName}`;
        const matched = list.find((item) => item.DESCRIPTION === menuName
          || item.DESCRIPTION === fullMenuName);
        if (matched) {
          return {
            menuName,
            menuCode: String(matched.MENU_CODE),
            registered: true,
            checked: false,
            sortIndex,
          };
        }
        while (usedCodes[String(this.parentCode * 100 + seq)]) {
          seq += 1;
        }
        const menuCode = String(this.parentCode * 100 + seq);
        usedCodes[menuCode] = true;
        seq += 1;
        return {
          menuName,
          menuCode,
          registered: false,
          checked: true,
          sortIndex,
        };
      });

      // 未登记的排在前面（保持菜单原有顺序），已登记的按菜单码升序
      this.menuRows = rows.sort((rowA, rowB) => {
        if (rowA.registered !== rowB.registered) {
          return rowA.registered ? 1 : -1;
        }
        if (rowA.registered) {
          return Number(rowA.menuCode) - Number(rowB.menuCode);
        }
        return rowA.sortIndex - rowB.sortIndex;
      });
    },
    async submitMenuPermission () {
      const checkedRows = this.menuRows.filter((row) => row.checked && !row.registered);
      if (!checkedRows.length) {
        this.$message.show({ text: '请先勾选需要新增的菜单', type: 'warning' });
        return;
      }
      const emptyRow = checkedRows.find((row) => !String(row.menuCode).trim());
      if (emptyRow) {
        this.$message.show({ text: `「${emptyRow.menuName}」的 MENU_CODE 不能为空`, type: 'warning' });
        return;
      }

      this.submitting = true;
      let successCount = 0;
      const failedList = [];
      for (let i = 0; i < checkedRows.length; i += 1) {
        const row = checkedRows[i];
        try {
          await addFrontMenu({
            MENU_CODE: String(row.menuCode).trim(),
            DESCRIPTION: row.menuName,
          });
          successCount += 1;
        } catch (error) {
          failedList.push(`${row.menuName}：${error}`);
        }
      }
      this.submitting = false;

      if (failedList.length) {
        this.$message.show({
          text: `新增完成：成功 ${successCount} 条，失败 ${failedList.length} 条。${failedList.join('；')}`,
          type: 'error',
        });
      } else {
        this.$message.show({ text: `新增成功 ${successCount} 条`, type: 'success' });
      }
      this.loadMenuPermission();
    },
  },
};
</script>

<style scoped>
.dev-tool-entry {
  position: fixed;
  right: 24px;
  bottom: 90px;
  z-index: 3000;
  padding: 8px 16px;
  border-radius: 18px;
  background: #464c5b;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.dev-tool-entry:hover {
  background: #2d8cf0;
}

.dev-tool-tip {
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
}

.dev-tool-tip-warn {
  color: #e6a23c;
}

.dev-tool-registered {
  color: #909399;
}

.dev-tool-new {
  color: #67c23a;
}

.dev-tool-footer {
  margin-top: 12px;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/components/base/DevTool.vue